/**
 * JSON Schema Types
 *
 * These define the output format of the transformation.
 * This is the "contract" between the package and host apps.
 *
 * All types are inferred from Zod schemas for maximum type safety.
 */

// Re-export all Zod-inferred types
export type {
  // Layout Types
  QuestionText,
  TextItem,
  WidgetRef,
  HighlightedText,
  ColorText,
  BoldText,
  ImageItem,
  SpaceItem,
  Linebreak,
  OperatorItem,
  GroupItem,
  TableItem,
  TableRowItem,
  TableCellItem,
  NumberLinePositionItem,
  NumberLineCounterItem,
  NumberLineHighlightItem,
  NumberLineCursorItem,
  NumberLineArrowItem,
  NumberLineItem,
  VopCellItem,
  VopDigitCellItem,
  VopLabelCellItem,
  VopInputCellItem,
  VopEmptyCellItem,
  VopColorCellItem,
  VopBoldCellItem,
  VopHighlightCellItem,
  VopRowItem,
  VopCarryRowItem,
  VopSeparatorItem,
  VerticalOperationItem,
  ClockItem,
  LayoutItem,

  // Widget Types
  InlineInputJSON,
  DragOptionsJSON,
  RadioOptionsJSON,
  NumericKeypadJSON,
  FractionJSON,
  MatcherItemContent,
  MatcherRowItem,
  MatcherWidgetJSON,
  RulerWidgetJSON,
  ProtractorWidgetJSON,
  SetSquareWidgetJSON,
  WidgetJSON,

  // Validation Types
  ValidationJSON,

  // Meta Types
  ExerciseMeta,

  // Main Exercise Type
  ExerciseJSON,
  ValidatedExerciseJSON,
} from './zod-schemas';

// Re-export Zod schemas for runtime validation
export {
  QuestionTextSchema,
  TextItemSchema,
  WidgetRefSchema,
  HighlightedTextSchema,
  ColorTextSchema,
  BoldTextSchema,
  ImageItemSchema,
  SpaceItemSchema,
  LinebreakSchema,
  OperatorItemSchema,
  GroupItemSchema,
  TableItemSchema,
  TableRowItemSchema,
  TableCellItemSchema,
  NumberLinePositionItemSchema,
  NumberLineCounterItemSchema,
  NumberLineHighlightItemSchema,
  NumberLineCursorItemSchema,
  NumberLineArrowItemSchema,
  NumberLineItemSchema,
  VopCellItemSchema,
  VopColorCellItemSchema,
  VopBoldCellItemSchema,
  VopHighlightCellItemSchema,
  VopRowItemSchema,
  VopCarryRowItemSchema,
  VopSeparatorItemSchema,
  VerticalOperationItemSchema,
  ClockItemSchema,
  LayoutItemSchema,
  InlineInputJSONSchema,
  DragOptionsJSONSchema,
  RadioOptionsJSONSchema,
  NumericKeypadJSONSchema,
  FractionJSONSchema,
  MatcherItemContentSchema,
  MatcherRowItemSchema,
  MatcherWidgetJSONSchema,
  RulerWidgetSchema,
  ProtractorWidgetSchema,
  SetSquareWidgetSchema,
  WidgetJSONSchema,
  ValidationJSONSchema,
  ExerciseMetaSchema,
  ExerciseJSONSchema,
  validateExerciseJSON,
  validateExerciseJSONSafe,
  validateWidgetJSON,
  validateWidgetJSONSafe,
} from './zod-schemas';

import type { LayoutItem, WidgetJSON, GroupItem } from './zod-schemas';
import { JSON_TYPES } from '../constants';

// ═══════════════════════════════════════════════════════════════════════════
// Type Guards
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Type guard for QuestionText
 */
export function isQuestionText(item: LayoutItem): item is Extract<LayoutItem, { type: 'question-text' }> {
  return item.type === JSON_TYPES.QUESTION_TEXT;
}

/**
 * Type guard for TextItem
 */
export function isTextItem(item: LayoutItem): item is Extract<LayoutItem, { type: 'text' }> {
  return item.type === JSON_TYPES.TEXT;
}

/**
 * Type guard for WidgetRef
 */
export function isWidgetRef(item: LayoutItem): item is Extract<LayoutItem, { type: 'widget-ref' }> {
  return item.type === JSON_TYPES.WIDGET_REF;
}

/**
 * Type guard for HighlightedText
 */
export function isHighlightedText(item: LayoutItem): item is Extract<LayoutItem, { type: 'highlighted-text' }> {
  return item.type === JSON_TYPES.HIGHLIGHTED_TEXT;
}

/**
 * Type guard for ColorText
 */
export function isColorText(item: LayoutItem): item is Extract<LayoutItem, { type: 'color-text' }> {
  return item.type === JSON_TYPES.COLOR_TEXT;
}

/**
 * Type guard for BoldText
 */
export function isBoldText(item: LayoutItem): item is Extract<LayoutItem, { type: 'bold-text' }> {
  return item.type === JSON_TYPES.BOLD_TEXT;
}

/**
 * Type guard for ImageItem
 */
export function isImageItem(item: LayoutItem): item is Extract<LayoutItem, { type: 'image' }> {
  return item.type === JSON_TYPES.IMAGE;
}

/**
 * Type guard for SpaceItem
 */
export function isSpaceItem(item: LayoutItem): item is Extract<LayoutItem, { type: 'space' }> {
  return item.type === JSON_TYPES.SPACE;
}

/**
 * Type guard for Linebreak
 */
export function isLinebreak(item: LayoutItem): item is Extract<LayoutItem, { type: 'linebreak' }> {
  return item.type === JSON_TYPES.LINEBREAK;
}

/**
 * Type guard for GroupItem
 */
export function isGroupItem(item: LayoutItem): item is GroupItem {
  return item.type === JSON_TYPES.GROUP;
}

/**
 * Type guard for NumberLineItem
 */
export function isNumberLineItem(item: LayoutItem): item is Extract<LayoutItem, { type: 'number-line' }> {
  return item.type === JSON_TYPES.NUMBER_LINE;
}

/**
 * Type guard for OperatorItem
 */
export function isOperatorItem(item: LayoutItem): item is Extract<LayoutItem, { type: 'operator' }> {
  return item.type === JSON_TYPES.OPERATOR;
}

/**
 * Type guard for TableItem
 */
export function isTableItem(item: LayoutItem): item is Extract<LayoutItem, { type: 'table' }> {
  return item.type === JSON_TYPES.TABLE;
}

/**
 * Type guard for TableRowItem
 */
export function isTableRowItem(item: LayoutItem): item is Extract<LayoutItem, { type: 'table-row' }> {
  return item.type === JSON_TYPES.TABLE_ROW;
}

/**
 * Type guard for TableCellItem
 */
export function isTableCellItem(item: LayoutItem): item is Extract<LayoutItem, { type: 'table-cell' }> {
  return item.type === JSON_TYPES.TABLE_CELL;
}

/**
 * Type guard for VerticalOperationItem
 */
export function isVerticalOperationItem(item: LayoutItem): item is Extract<LayoutItem, { type: 'vertical-operation' }> {
  return item.type === JSON_TYPES.VERTICAL_OPERATION;
}

/**
 * Type guard for ClockItem
 */
export function isClockItem(item: LayoutItem): item is Extract<LayoutItem, { type: 'clock' }> {
  return item.type === JSON_TYPES.CLOCK;
}

/**
 * Type guard for InlineInputJSON
 */
export function isInlineInputJSON(widget: WidgetJSON): widget is Extract<WidgetJSON, { type: 'inline-input' }> {
  return widget.type === JSON_TYPES.INLINE_INPUT;
}

/**
 * Type guard for DragOptionsJSON
 */
export function isDragOptionsJSON(widget: WidgetJSON): widget is Extract<WidgetJSON, { type: 'drag-options' }> {
  return widget.type === JSON_TYPES.DRAG_OPTIONS;
}

/**
 * Type guard for InlineDragOptionJSON
 */
export function isInlineDragOptionJSON(widget: WidgetJSON): widget is Extract<WidgetJSON, { type: 'inline-drag-option' }> {
  return widget.type === JSON_TYPES.INLINE_DRAG_OPTION;
}

/**
 * Type guard for RadioOptionsJSON
 */
export function isRadioOptionsJSON(widget: WidgetJSON): widget is Extract<WidgetJSON, { type: 'radio-options' }> {
  return widget.type === JSON_TYPES.RADIO_OPTIONS;
}

/**
 * Type guard for NumericKeypadJSON
 */
export function isNumericKeypadJSON(widget: WidgetJSON): widget is Extract<WidgetJSON, { type: 'numeric-keypad' }> {
  return widget.type === JSON_TYPES.NUMERIC_KEYPAD;
}

/**
 * Type guard for FractionJSON
 */
export function isFractionJSON(widget: WidgetJSON): widget is Extract<WidgetJSON, { type: 'fraction' }> {
  return widget.type === JSON_TYPES.FRACTION;
}

/**
 * Type guard for MatcherWidgetJSON
 */
export function isMatcherWidget(widget: WidgetJSON): widget is Extract<WidgetJSON, { type: 'matcher' }> {
  return widget.type === JSON_TYPES.MATCHER;
}

/**
 * Type guard for RulerWidgetJSON
 */
export function isRulerWidget(widget: WidgetJSON): widget is Extract<WidgetJSON, { type: 'ruler' }> {
  return widget.type === JSON_TYPES.RULER;
}

/**
 * Type guard for ProtractorWidgetJSON
 */
export function isProtractorWidget(widget: WidgetJSON): widget is Extract<WidgetJSON, { type: 'protractor' }> {
  return widget.type === JSON_TYPES.PROTRACTOR;
}

/**
 * Type guard for SetSquareWidgetJSON
 */
export function isSetSquareWidget(widget: WidgetJSON): widget is Extract<WidgetJSON, { type: 'set-square' }> {
  return widget.type === JSON_TYPES.SET_SQUARE;
}
